import { memo, type ReactNode } from "react";

/**
 * Shared header block for every onboarding step: a mono-uppercase
 * eyebrow with a pulsing rust dot, a display heading, and an optional
 * lede paragraph. Each line uses one of the `_co_onb_settle_*` classes
 * so they stagger in over ~700ms when the step mounts.
 *
 * Sizing is fully driven by the per-variant CSS custom properties set
 * by `OnboardingOverlay` on the frame element (`--co-onb-fs-*`,
 * `--co-onb-stack-*`). The same component therefore looks right in a
 * generous splash card and a 520px-wide corner coach card without
 * needing per-anchor branches.
 *
 * Layout is left-aligned (coach-card aesthetic). Steps that need a
 * centered hero composition opt into centering at their own
 * component level.
 *
 * The heading is a ReactNode so callers can embed the Cohesive italic
 * flourish (Instrument Serif) inline, matching the brand voice from
 * step to step.
 */

interface StepHeaderProps {
  eyebrow?: string;
  heading: ReactNode;
  headingClassName?: string;
  lede?: ReactNode;
  align?: "left" | "center";
}

export const StepHeader = memo(function StepHeader({
  eyebrow,
  heading,
  headingClassName = "_co_onb_settle_2",
  lede,
  align = "left",
}: StepHeaderProps) {
  const isCentered = align === "center";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isCentered ? "center" : "flex-start",
        textAlign: isCentered ? "center" : "left",
        flex: "0 0 auto",
        width: "100%",
        gap: "var(--co-onb-stack-sm)",
      }}
    >
      {eyebrow != null && (
        <div
          className="_co_onb_settle_1"
          style={{
            fontFamily:
              "ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace",
            fontSize: "var(--co-onb-fs-eyebrow)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--co-fg-muted)",
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--co-onb-stack-sm)",
            marginBottom: "var(--co-onb-stack-xs)",
          }}
        >
          <span
            className="_co_onb_dot"
            style={{
              width: "var(--co-onb-dot)",
              height: "var(--co-onb-dot)",
              borderRadius: "50%",
              background: "var(--co-rust)",
              display: "inline-block",
            }}
          />
          {eyebrow}
        </div>
      )}

      <h1
        className={headingClassName}
        style={{
          margin: 0,
          maxWidth: "var(--co-onb-content-max)",
          fontSize: "var(--co-onb-fs-heading)",
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          fontWeight: 500,
          color: "var(--co-fg)",
        }}
      >
        {heading}
      </h1>

      {lede != null && (
        <p
          className="_co_onb_settle_3"
          style={{
            margin: "var(--co-onb-stack-sm) 0 0",
            maxWidth: "var(--co-onb-content-max)",
            fontSize: "var(--co-onb-fs-lede)",
            lineHeight: 1.5,
            color: "var(--co-fg-muted)",
            fontWeight: 400,
          }}
        >
          {lede}
        </p>
      )}
    </div>
  );
});

/**
 * Inline italic flourish used inside step headings. Wraps a word in
 * Instrument Serif italic with a tighter letter-spacing — same treatment
 * across every step so the brand voice carries through the flow.
 */
export const Flourish = memo(function Flourish({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <span
      style={{
        fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Georgia', serif",
        fontStyle: "italic",
        fontWeight: 400,
        letterSpacing: "-0.01em",
        color: "var(--co-fg)",
      }}
    >
      {children}
    </span>
  );
});
